import {classifyAuthError, resolveConsumer, tryDecodeJwtPayload, CONSUMER_UNKNOWN} from "./consumer";
import {buildTelemetryContext} from "./telemetryContext";

function getAgent() {
    return typeof window !== 'undefined' ? window.newrelic : undefined;
}

export function setNrAttribute(name, value) {
    const agent = getAgent();
    if (!agent || typeof agent.setCustomAttribute !== 'function') return;
    try {
        agent.setCustomAttribute(name, value);
    } catch (_e) {
        // New Relic must never break the app
    }
}

export function noticeNrError(err, attributes) {
    const agent = getAgent();
    if (!agent || typeof agent.noticeError !== 'function') return;
    try {
        agent.noticeError(err instanceof Error ? err : new Error(String(err)), attributes);
    } catch (_e) {
        // New Relic must never break the app
    }
}

/**
 * Registers the build version (application.version on every event), so a canary release can be compared
 * against the stable one in New Relic. REACT_APP_VERSION is set by the CI build.
 */
export function reportAppVersion(version) {
    const agent = getAgent();
    if (!version || !agent || typeof agent.setApplicationVersion !== 'function') return;
    try {
        agent.setApplicationVersion(version);
    } catch (_e) {
        // New Relic must never break the app
    }
}

/**
 * Registers the Headless consumer on New Relic (catchup.consumer, catchup.consumer_id, catchup.consumer_raw).
 */
export function reportConsumer(info) {
    const data = info || {};
    setNrAttribute('catchup.consumer', data.consumer || CONSUMER_UNKNOWN);
    setNrAttribute('catchup.consumer_id', data.consumerId || data.consumer_id || CONSUMER_UNKNOWN);
    if (data.consumerRaw || data.consumer_raw) {
        setNrAttribute('catchup.consumer_raw', data.consumerRaw || data.consumer_raw);
    }
}

function readStoredUser() {
    try {
        return JSON.parse(localStorage.getItem('user-info') || '{}') || {};
    } catch (_e) {
        return {};
    }
}

/**
 * Registers runtime, entrypoint, tenant, environment and consumer attributes for the current route/context.
 */
export function syncTelemetryContext({isEmbedMode, pathname}) {
    const context = buildTelemetryContext({
        isEmbedMode,
        pathname,
        user: readStoredUser(),
        deployEnv: process.env.REACT_APP_DEPLOY_ENV,
    });
    Object.keys(context).forEach((name) => setNrAttribute(name, context[name]));
    reportAppVersion(process.env.REACT_APP_VERSION);
}

/**
 * Registers the telemetry context straight from window.location (HashRouter), before React renders,
 * so the first PageView/AJAX harvested by the agent already carry the segmentation attributes.
 */
export function syncTelemetryContextFromLocation(loc) {
    const location = loc || (typeof window !== 'undefined' ? window.location : null);
    if (!location) return;
    const pathname = String(location.hash || '').replace(/^#/, '').split('?')[0] || '/';
    let inIframe = false;
    try {
        inIframe = window.self !== window.top;
    } catch (_e) {
        inIframe = true;
    }
    syncTelemetryContext({isEmbedMode: pathname.indexOf('embeddable') !== -1 || inIframe, pathname});
}

/**
 * Registers a Headless authentication failure, keeping the consumer when the payload is decodable.
 */
export function reportAuthFailure(jwtToken, err) {
    const payload = tryDecodeJwtPayload(jwtToken);
    const consumerInfo = resolveConsumer(payload);
    const authStatus = classifyAuthError(err, payload);

    reportConsumer(consumerInfo);
    setNrAttribute('catchup.auth_status', authStatus);
    noticeNrError(err, {
        'catchup.auth_status': authStatus,
        'catchup.consumer': consumerInfo.consumer,
        'catchup.consumer_id': consumerInfo.consumerId,
    });
}
